import React from "react";
import { Table } from "react-bootstrap";
import { Skeleton, Tooltip } from "@mui/material";
import { LIST_COLUMNS, getDeliveryStatusChip } from "./Constants";

function Chip({ label, className, title }) {
  return (
    <span className={className} title={title || undefined}>
      {label}
    </span>
  );
}

function StatusCell({ row }) {
  const chip = getDeliveryStatusChip(row);
  return (
    <Chip label={chip.label} className={chip.className} title={chip.title} />
  );
}

function ErrorCell({ row }) {
  if (!row.fullError) return "—";
  if (row.errorTruncated) {
    return (
      <Tooltip title={row.fullError} arrow>
        <span className="rda-error-cell">{row.displayError}</span>
      </Tooltip>
    );
  }
  return <span className="rda-error-cell">{row.displayError}</span>;
}

function AuditListTable({
  rows,
  loading,
  fetching,
  emptyMessage = "No Data Available",
  onRowSelect,
}) {
  const showInitialSkeleton = loading && (!rows || rows.length === 0);
  const showRows = !showInitialSkeleton && rows?.length > 0;
  const showEmpty = !loading && !fetching && (!rows || rows.length === 0);
  const showTableOverlay = (loading || fetching) && rows?.length > 0;

  const handleRowSelect = (row) => {
    if (!onRowSelect || !row.encounterId || !row.orgId) return;
    onRowSelect({ orgId: row.orgId, encounterId: row.encounterId });
  };

  return (
    <div
      className={`rda-table-wrap table-wrap${
        showTableOverlay ? " rda-table-wrap--busy" : ""
      }`}
    >
      <Table responsive className="rda-table">
        <thead>
          <tr>
            {LIST_COLUMNS.map((label) => (
              <th key={label}>{label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {showInitialSkeleton
            ? [1, 2, 3, 4, 5, 6].map((n) => (
                <tr key={`sk-${n}`}>
                  {LIST_COLUMNS.map((col) => (
                    <td key={col}>
                      <Skeleton animation="wave" />
                    </td>
                  ))}
                </tr>
              ))
            : null}
          {showRows
            ? rows.map((row) => {
                const selectable = Boolean(
                  onRowSelect && row.encounterId && row.orgId
                );
                return (
                  <tr
                    key={row.id}
                    className={selectable ? "rda-table-row--clickable" : undefined}
                    onClick={() => handleRowSelect(row)}
                    onKeyDown={(e) => {
                      if (!selectable) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleRowSelect(row);
                      }
                    }}
                    tabIndex={selectable ? 0 : undefined}
                    role={selectable ? "button" : undefined}
                    aria-label={
                      selectable
                        ? `View report delivery summary for encounter ${row.encounterId}`
                        : undefined
                    }
                  >
                    <td>{row.displayTime}</td>
                    <td>{row.displayOrg}</td>
                    <td>{row.encounterId || "—"}</td>
                    <td>{row.displayRegistry}</td>
                    <td>{row.displayDeliveryType}</td>
                    <td>{row.displayProtocol}</td>
                    <td>{row.displayFileType}</td>
                    <td>
                      <StatusCell row={row} />
                    </td>
                    <td title={row.triggeredByTitle}>{row.displayTriggeredBy}</td>
                    <td>
                      <ErrorCell row={row} />
                    </td>
                  </tr>
                );
              })
            : null}
          {showEmpty ? (
            <tr>
              <td colSpan={LIST_COLUMNS.length} className="rda-empty">
                {emptyMessage}
              </td>
            </tr>
          ) : null}
        </tbody>
      </Table>
    </div>
  );
}

export default AuditListTable;
